// Story 022 AC4 — Filmstrip rail rendered inside the Split-View overlay.
//
// Mirrors the visible affordance of the page-level FilmstripWidget
// (role="listbox" + role="option" thumbnails) so the analyst keeps the
// muscle-memory of "click a thumb to pick a candidate". When the
// analyst clicks here we route directly to `splitViewStore.setSecondaryImage`
// — no detour through `imageActivationStore` and no SplitViewOverlay
// subscription glitch.
//
// While split-view is active, the page-level filmstrip is hidden via
// CSS `display: none` (see SplitViewOverlay's effect) so this rail is
// the only filmstrip in the DOM — Playwright's `getByRole('option', ...)`
// resolves uniquely without strict-mode failure.
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import type { ImageRef } from "@aeriosiq/types";
import { useImagesQuery } from "../queries/useImagesQuery.js";
import { useSplitViewStore } from "./splitViewStore.js";

const THUMB_W = 140;
const THUMB_H = 96;

interface SplitViewFilmstripProps {
  dataAcquisitionId: string;
}

export function SplitViewFilmstrip({
  dataAcquisitionId,
}: SplitViewFilmstripProps): React.ReactElement {
  const query = useImagesQuery(dataAcquisitionId);
  const primaryImageId = useSplitViewStore((s) => s.primaryImageId);
  const secondaryImageId = useSplitViewStore((s) => s.secondaryImageId);
  const setSecondaryImage = useSplitViewStore((s) => s.setSecondaryImage);

  const items: ImageRef[] = query.data?.items ?? [];
  return (
    <Stack
      role="listbox"
      aria-label="Split-view candidate images"
      direction="row"
      spacing={1}
      sx={{
        px: 1,
        py: 1,
        overflowX: "auto",
        borderTop: 1,
        borderColor: "divider",
        flexShrink: 0,
        bgcolor: "background.paper",
      }}
    >
      {items.map((it) => {
        const isPrimary = it.imageId === primaryImageId;
        const isSecondary = it.imageId === secondaryImageId;
        return (
          <ButtonBase
            key={it.imageId}
            role="option"
            aria-selected={isSecondary}
            aria-label={`Load image ${it.basename}`}
            data-image-id={it.imageId}
            onClick={() => {
              if (isPrimary) return; // can't pick the primary itself
              setSecondaryImage(it.imageId);
            }}
            sx={{
              width: THUMB_W,
              height: THUMB_H,
              borderRadius: 1,
              border: 2,
              borderColor: isSecondary
                ? "primary.main"
                : isPrimary
                  ? "warning.main"
                  : "divider",
              opacity: isPrimary ? 0.5 : 1,
              overflow: "hidden",
              flexShrink: 0,
              position: "relative",
              "&:focus-visible": {
                outline: 2,
                outlineColor: "primary.main",
                outlineOffset: 2,
              },
            }}
          >
            <Box
              component="img"
              src={it.urls.thumb120}
              alt=""
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                left: 4,
                bottom: 4,
                px: 0.5,
                bgcolor: "rgba(0,0,0,0.5)",
                color: "#fff",
                borderRadius: 0.5,
              }}
            >
              {isPrimary ? "Primary" : it.basename.slice(0, 12)}
            </Typography>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}
